import { MouseEventHandler, SyntheticEvent, useRef } from 'react';
import styled from 'styled-components';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Close16 } from 'ui/icons/action/Close16';
import { NegativeFillDiamond } from 'ui/icons/status/NegativeFillDiamond';
import { colors, duration, easing, fontSizes, fontWeights, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { NoOp } from 'utils/noop';
import { useTranslations } from '../hooks/useTranslations';
import { Asset, NewImage, PromiseStatus } from '../types';
import { canRemovePendingAsset } from '../utils/images';
import { ItemDetails } from './ItemDetails';
import { FolderClosed } from 'ui/icons/action/FolderClosed';
import { Logo, LogoColors } from 'ui/logo';
import { ROOT_FOLDER_NAME } from '../constants';

const ListItemWrapper = styled.div<{
  onClick?: MouseEventHandler;
  $isSelected?: boolean;
}>`
  border-color: transparent;
  border-style: solid;
  border-width: ${spacing.single};
  column-gap: ${spacing.base};
  display: flex;
  padding: ${spacing.plusHalf} ${spacing.base};
  position: relative;
  transition: all ${duration.short} ${easing.entrance};
  &:nth-child(odd) {
    background-color: ${({ $isSelected }) =>
      $isSelected ? colors.backgroundInteractive : colors.backgroundSecondary};
  }
  &:nth-child(even) {
    background-color: ${({ $isSelected }) =>
      $isSelected ? colors.backgroundInteractive : colors.backgroundPrimary};
  }
  ${({ $isSelected, onClick }) =>
    onClick
      ? `
      cursor: pointer;
      user-select: none;
      &:hover {
        background-color: ${$isSelected ? colors.backgroundInteractive : colors.backgroundTertiary};
      }
    `
      : `
      cursor: default;
    `}
  ${({ $isSelected }) =>
    $isSelected
      ? `
      border-color: ${colors.borderInteractive};
      color: ${colors.iconInteractive};
    `
      : ``}
`;

// left section of the list item containing the preview image
const ListItemPreview = styled.div<{ $imgSrc: string }>`
  background-image: url(${({ $imgSrc }) => $imgSrc});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: ${spacing.threeEights};
  flex: 0 0 auto;
  height: ${pxToRem(48)};
  width: ${pxToRem(56)};
`;

// left section of the list item containing an error state image icon (when image processing fails)
const ListItemPreviewError = styled.div`
  align-items: center;
  background-color: ${colors.grey5};
  border-radius: ${spacing.threeEights};
  color: ${colors.negative100};
  display: flex;
  flex: 0 0 auto;
  height: ${pxToRem(48)};
  justify-content: center;
  width: ${pxToRem(56)};
`;

// center section of the list item containing the name & file info (progress bar || file data || error)
const ListItemData = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: center;
  width: 1px;
  gap: ${pxToRem(4)};
`;

// file name within ListItemData
const ListItemTitle = styled.div`
  font-size: ${fontSizes.s14};
  font-weight: ${fontWeights.medium};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// file data (size, width, height, ... || error reponse) within LisItemData
const ListItemDetail = styled.div<{
  $isSelected?: boolean;
}>`
  color: ${({ $isSelected }) => ($isSelected ? colors.textInteractive : colors.textSecondary)};
  display: block;
  font-size: ${fontSizes.s12};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ListItemFolder = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
`;

const FolderText = styled.span`
 font-size: ${fontSizes.s12};
 font-weight: ${fontWeights.regular};
`;

// file data (size, width, height, ... || error reponse) within LisItemData
const ListItemDetailError = styled.div`
  align-items: flex-start;
  color: ${colors.negative100};
  display: flex;
  font-size: ${fontSizes.s12};
  svg {
    flex: 0 0 auto;
    margin: ${spacing.single} ${spacing.quarter} 0 0;
  }
`;

// uploading progress bar within LisItemData
const ProgressBar = styled.div`
  background-color: ${colors.grey25};
  border-radius: ${spacing.eighth};
  height: ${spacing.threeEights};
  margin-top: ${spacing.base};
  overflow: hidden;
  width: 100%;
`;

const ProgressBarValue = styled.div`
  background-color: ${colors.green50};
  height: 100%;
  transition: width 1s;
`;

// right section containing the upload abort button
const ListItemCancel = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
`;

export const testIds = {
  item: 'cl-asset-list-item',
  itemDetail: 'cl-asset-list-item-detail',
  itemDetailError: 'cl-asset-list-item-detail-error',
  itemDetailProgress: 'cl-asset-list-item-detail-progress',
  itemPending: 'cl-asset-list-item-pending',
  itemPendingAbortBtn: 'cl-asset-list-item-pending-abort-btn',
  itemPendingCancelBtn: 'cl-asset-list-item-pending-cancel-btn',
  itemPendingPreviewError: 'cl-asset-list-item-pending-preview-error',
  itemPreview: 'cl-asset-list-item-preview',
  itemTitle: 'cl-asset-list-item-title',
};

// an asset that has not yet successfully uploaded
export const ListItemPending = ({
  image,
  isSelected = false,
  destinationFolderName,
  onClick,
  onDelete,
}: {
  image: NewImage;
  isSelected?: boolean;
  destinationFolderName?: string;
  onClick?: MouseEventHandler;
  onDelete?: () => void;
}) => {
  const { t } = useTranslations();
  const isProcessRejected = image.processStatus === PromiseStatus.Rejected;
  const isUploadRejected = [PromiseStatus.Aborted, PromiseStatus.Rejected].includes(
    image.uploadStatus,
  );
  const errorMessageKey = isProcessRejected
    ? `errors.processImage.${image.processError}`
    : `errors.uploadAsset.${image.uploadError}`;
  // the previewUrl will not change during the life of the component, so generate it only once
  const previewUrl = useRef(isProcessRejected ? '' : URL.createObjectURL(image?.file?.file));
  const canRemove = canRemovePendingAsset(image) && !image.abortUpload;

  const handleAbort = (e: SyntheticEvent, abort?: NoOp) => {
    e.stopPropagation();
    abort?.();
  };

  const renderDetail = (isSelected?: boolean) => {
    if (isProcessRejected || isUploadRejected) {
      return (
        <ListItemDetailError data-test={testIds.itemDetailError}>
          <NegativeFillDiamond isColorInherited />
          <span>{image.validationError || t(errorMessageKey)}</span>
        </ListItemDetailError>
      );
    } else if (image.uploadStatus === PromiseStatus.Pending) {
      return (
        <ProgressBar>
          <ProgressBarValue
            data-test={testIds.itemDetailProgress}
            style={{ width: `${image.uploadPct}%` }}
          />
        </ProgressBar>
      );
    }
    return (
      <ListItemDetail data-test={testIds.itemDetail} $isSelected={isSelected}>
        <ItemDetails metadata={image.file} />
      </ListItemDetail>
    );
  };

  return (
    <ListItemWrapper $isSelected={isSelected} data-test={testIds.itemPending} onClick={onClick}>
      {isProcessRejected ? (
        <ListItemPreviewError data-test={testIds.itemPendingPreviewError}>
          <Logo color={LogoColors.BLACK} height={24} />
        </ListItemPreviewError>
      ) : (
        <ListItemPreview data-test={testIds.itemPreview} $imgSrc={previewUrl.current} />
      )}
      <ListItemData>
        <ListItemTitle data-test={testIds.itemTitle}>{image.file?.name}</ListItemTitle>
        {renderDetail(isSelected)}
        {destinationFolderName && (
          <ListItemFolder>
            {destinationFolderName !== ROOT_FOLDER_NAME && <FolderClosed />}
            <span>{destinationFolderName}</span>
          </ListItemFolder>
        )}
      </ListItemData>
      {canRemove && onDelete && (
        <ListItemCancel>
          <IconButton
            data-test={testIds.itemPendingCancelBtn}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.GHOST}
          >
            <Close16 />
          </IconButton>
        </ListItemCancel>
      )}
      {image.abortUpload && (
        <ListItemCancel>
          <IconButton
            data-test={testIds.itemPendingAbortBtn}
            onClick={(e) => handleAbort(e, image.abortUpload)}
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.GHOST}
          >
            <Close16 />
          </IconButton>
        </ListItemCancel>
      )}
    </ListItemWrapper>
  );
};

// an existing asset (successfully uploaded and part of the creative library)
export const ListItem = ({
  asset,
  isSelected = false,
  destinationFolderName,
  onClick,
  onDelete,
}: {
  asset: Asset;
  isSelected?: boolean;
  destinationFolderName?: string;
  onClick?: MouseEventHandler;
  onDelete?: () => void;
}) => {
  return (
    <ListItemWrapper onClick={onClick} data-test={testIds.item} $isSelected={isSelected}>
      <ListItemPreview $imgSrc={asset.url} data-test={testIds.itemPreview} />
      <ListItemData>
        <ListItemTitle data-test={testIds.itemTitle}>{asset.assetName}</ListItemTitle>
        <ListItemDetail data-test={testIds.itemDetail} $isSelected={isSelected}>
          <ItemDetails metadata={asset} />
        </ListItemDetail>
        {destinationFolderName && (
          <ListItemFolder>
            {destinationFolderName !== ROOT_FOLDER_NAME && <FolderClosed />}
            <FolderText>{destinationFolderName}</FolderText>
          </ListItemFolder>
        )}
      </ListItemData>
      {onDelete && (
        <ListItemCancel>
          <IconButton
            data-test={testIds.itemPendingCancelBtn}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.GHOST}
          >
            <Close16 />
          </IconButton>
        </ListItemCancel>
      )}
    </ListItemWrapper>
  );
};
